import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  selectDraftConfig,
  setCountRender,
  setDraftPlayersAction,
  setTradeValue,
  uniqPosition,
  setPauseId,
  delPauseId,
  addPlayerToTeam,
  fanaticModeBefore,
  setTeamUniqPositionFanaticChallenge,
} from '../../app/features/draftConfig/draftConfigSlice';
import { getTradeValue } from '../../app/features/draftConfig/drafConfigAction';
import { postDraftCountForTeam } from '../../app/features/draftCount/draftCountActions';
import { setHistoryBoard } from '../../app/features/teamNeeds/teamNeedsSlice';
import {
  selectTrades,
  setChangeTrades,
  setRandomFlag,
  setTradeRandomCount,
  setTradeRandomId,
} from '../../app/features/trades/tradesSlice';
import {
  delPlayersDraft,
  fetchAllPlayers,
  sortPlayersByTeam,
  selectPlayersDraft,
  updateTeamADP,
  refreshTeamBoard,
} from '../../app/features/playersDraft/playersDraftSlice';
import { boardAvailabilityRank, calculateADP } from '../../utils/utils';
import { selectDraftResult, setDraftResultAction } from '../../app/features/draftResult/draftResultSlice';
import { draftDisableSettings, draftPlainSettings } from '../../components/DraftViewAsign/DraftSettings';
import { PLAYER_MAX } from '../../config/config';
import { POSITIONS_COLOR } from '../../utils/constants';
import { isPositionUnique } from '../../utils/utils';
import TradeModal, { closeTrade, TRADE_ATTEMPTS } from './TradeModal';
import TeamPicksPanel from './TeamPicksPanel';
import YourPickModal from './YourPickModal';
import useSheetSwipe from './useSheetSwipe';
import {
  MOBILE_QUERY,
  MobileViewTabs,
  MobileViewTab,
  PageShell,
  ContentRow,
  BoardColumn,
  BoardHeader,
  BoardHeaderText,
  BoardScroll,
  PickPanelWrap,
  TopBar,
  TopBarRow,
  StartDraftBtn,
  CancelDraftBtn,
  TeamInfoLeft,
  TeamInfoLogo,
  TeamInfoName,
  BoardWrap,
  BoardLoaderWrap,
  BoardLoaderIcon,
  BoardLoaderTitle,
  BoardLoaderSub,
  RoundBanner,
  RoundBannerText,
  PickRow,
  PickLeft,
  PickNumCol,
  PickNumText,
  PickLogoWrap,
  PickLogo,
  PickNameCol,
  PickTeamName,
  PickRight,
  PickInner,
  PickSlotA,
  PickSlotB,
  OnTheClockBadge,
} from './DraftBoard.styles';

const PlayIcon = () => (
  <svg width="12" height="14" viewBox="0 0 10 12" fill="currentColor" aria-hidden="true">
    <path d="M0 0 L10 6 L0 12 Z" />
  </svg>
);

const PauseIcon = () => (
  <svg width="12" height="14" viewBox="0 0 10 12" fill="currentColor" aria-hidden="true">
    <rect x="0" y="0" width="3" height="12" />
    <rect x="7" y="0" width="3" height="12" />
  </svg>
);

const DraftBoard = ({ onCancel }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    teamSelect,
    teamSelectId,
    teamSelectIndex,
    round,
    tradeValue,
    countRender,
    teamPickIndex,
    teamPickIndexControl,
    pauseId,
    status,
    draftCardDepth,
    draftRandomnessTeam,
    roundBPA,
    roundDepth,
    teamUniqPosition,
    teamUniqPositionFanaticChallenge,
    fanaticIndexPosition,
    fanaticChallenge,
    fanaticPickId,
    fanaticPlayerBefore,
    fanaticMode,
    teamDraftPlayerChoose,
    draftPlayers,
    tradingSimulatorAction,
    timeSpeed,
    iterationSection,
  } = useSelector(selectDraftConfig);

  const { tradesTeams, changeTrades, tradeRandomId, randomAccepted } = useSelector(selectTrades);
  const playersDraft = useSelector(selectPlayersDraft);
  const draftResults = useSelector(selectDraftResult);

  const [headerH, setHeaderH] = useState(64);
  const [picksTab, setPicksTab] = useState('your');
  const [yourTeamId, setYourTeamId] = useState(null);
  const [showTrade, setShowTrade] = useState(false);
  // Rejected trade offers this draft, across every team — 10, then no more trading
  // (docs/trading.md). Kept here, not in the Trade tab, which unmounts when closed; a new
  // draft is a new DraftBoard, so it starts from zero.
  const [tradeRejections, setTradeRejections] = useState(0);
  const tradeAttemptsLeft = TRADE_ATTEMPTS - tradeRejections;
  const [isDrafting, setIsDrafting] = useState(false);
  const [showYourPick, setShowYourPick] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [thisId, setThisId] = useState(0);
  const headerRef = useRef(null);
  const lastPickedRef = useRef(null);
  const pickTimerRef = useRef(null);
  const isSortPendingRef = useRef(false);

  // Mobile layout (DraftBoard.styles MOBILE_QUERY): the column under the top bar shows the round
  // board or the Team Picks panel, picked by tabs, and the pick panel is a bottom sheet.
  // noSsr reads the query on the first render, so a phone never renders the desktop layout once.
  const isMobile = useMediaQuery(MOBILE_QUERY, { noSsr: true });
  const [mobileView, setMobileView] = useState('board'); // 'board' | 'picks'
  const [sheetOpen, setSheetOpen] = useState(false);
  // Height of the sheet's header, which is what shows while it's closed.
  const [sheetPeekH, setSheetPeekH] = useState(0);
  const sheetRef = useRef(null);
  const sheetHeaderRef = useRef(null);
  const sheetSwipe = useSheetSwipe({
    sheetRef, peek: sheetPeekH, open: sheetOpen, setOpen: setSheetOpen,
  });

  // Initial data load
  useEffect(() => {
    dispatch(getTradeValue(round));
    dispatch(fetchAllPlayers());
  }, [dispatch, round]);

  // Close the Trade tab when changeTrades flips; resume auto-pick if it was paused for trade.
  // pauseId is an array (setPauseId stores [id]) — compared as a string it never matched, so the
  // draft stayed paused after every trade until Resume Draft was pressed.
  useEffect(() => {
    if (changeTrades && showTrade) {
      setShowTrade(false);
      if (pauseId[0] === 'trade') dispatch(delPauseId());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeTrades]);

  // Navigate to result page when draft completes. The v2 board goes to the v2 result page
  // (/draft-result); the v1 board (DraftPlayer.jsx) goes to /draft-result-v1. Same slice feeds both.
  useEffect(() => {
    if (draftResults?.results?.length > 0) navigate('/draft-result');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftResults?.results]);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setHeaderH(el.offsetHeight));
    ro.observe(el);
    setHeaderH(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  // The sheet's header changes height — it loses the team row and tabs once the user is out of
  // picks — and the columns above leave exactly that much room at the bottom.
  useLayoutEffect(() => {
    const el = sheetHeaderRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSheetPeekH(el.offsetHeight));
    ro.observe(el);
    setSheetPeekH(el.offsetHeight);
    return () => ro.disconnect();
  }, [isMobile]);

  // Load players for the current pick position during simulation. Also re-runs when a trade hands
  // the pick on the clock to another team (the draft is paused for trades), so the pool is built
  // for the new owner's board — not left sorted, and sized, for the team that traded it away.
  const clockTeamName = tradeValue.results?.[countRender]?.round?.name;
  useEffect(() => {
    if (!isDrafting || !tradeValue.mouthing) return;
    const results = [...(tradeValue.results ?? [])].sort((a, b) => (a.index_position ?? a.index ?? 0) - (b.index_position ?? b.index ?? 0));
    if (countRender >= results.length) return;

    const team = results[countRender];
    const teamName = team.round.name;
    const manualLength = playersDraft.playerManualChoose?.length ?? 1;
    const isMyTeam = teamSelect.some((t) => t.name === teamName);
    const teamPosition = team.index_position ?? team.index;
    const playerCountGet = !isMyTeam
      ? (fanaticMode ? draftCardDepth + teamPosition : draftCardDepth + team.index + manualLength)
      : PLAYER_MAX;
    isSortPendingRef.current = true;
    dispatch(sortPlayersByTeam({ playerCountGet, teamName }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countRender, isDrafting, tradeValue.mouthing, clockTeamName]);

  // Reset the sort-pending guard whenever results change (fires after every sortPlayersByTeam)
  useEffect(() => {
    isSortPendingRef.current = false;
  }, [playersDraft.results]);

  // Signal draft complete → setDraftResultAction + setHistoryBoard
  useEffect(() => {
    if (!isDrafting || !tradeValue.mouthing) return;
    const iter = iterationSection?.iterationSection ?? [];
    const totalPicks = tradeValue.results?.length ?? 0;

    const buildHistoryData = (picks) => ({
      items: picks.map(({ round_index, count = null, tm, round, player, upPlayers }) => ({
        round_index: +(round_index?.split(' ')[0] ?? 0),
        count,
        team: tm,
        draft: round?.id,
        player: player?.id,
        position: player?.position,
        upPlayers,
      })),
    });

    // Draft total count, posted only when the whole draft is finished — a draft quit part way,
    // even after some iterations, counts nothing. Each selected team gets the number of
    // iterations: 1 for a plain draft; 5 / 10 / the fanatic intensity for a challenge or fanatic
    // draft (iter holds one milestone per iteration). The endpoint adds to the team's stored total.
    const addDraftCount = () => {
      const draftedCount = iter.length || 1;
      teamSelect.forEach((team) =>
        dispatch(postDraftCountForTeam({ teamId: team.draftCountId, draftedCount }))
      );
    };

    if (iter.length > 0 && iter.includes(countRender)) {
      const prevMilestone = iter[iter.indexOf(countRender) - 1] ?? 0;
      const iterPicks = draftPlayers.slice(prevMilestone, countRender);
      dispatch(setHistoryBoard(buildHistoryData(iterPicks)));
      if (countRender === iter.at(-1)) {
        addDraftCount();
        dispatch(setDraftResultAction(draftPlayers, teamSelect, round, teamPickIndexControl, draftRandomnessTeam));
        // No board save here — each pick is posted to the backend as it happens
        // (recordDraftPick), which owns the ADP math. Saving the whole board at the end would
        // overwrite what the server accumulated, and lose a concurrent user's draft.
      }
    } else if (countRender === totalPicks && iter.length === 0) {
      addDraftCount();
      dispatch(setDraftResultAction(draftPlayers, teamSelect, round, teamPickIndexControl, draftRandomnessTeam));
      dispatch(setHistoryBoard(buildHistoryData(draftPlayers)));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countRender]);

  // Cleanup pick timer on unmount
  useEffect(() => () => clearTimeout(pickTimerRef.current), []);

  // Auto-pick engine (mirrors DraftViewAsign.advancingSettings)
  const runAutoPick = useCallback(() => {
    const results = tradeValue.results ?? [];
    if (
      !isDrafting ||
      !tradeValue.mouthing ||
      playersDraft.loading ||
      isSortPendingRef.current ||
      // The pool must have been filtered for THIS pick. isSortPendingRef alone is not enough: the
      // load effect is declared above the results watcher, so when a single batch changes both
      // countRender and results — which is what the user's own pick handler does, dispatching
      // delPlayersDraft and setCountRender together — the flag is set true and then cleared in the
      // same pass, letting the previous team's pool through. That is how a team drafted a player it
      // had already taken twice, on the pick right after the user's.
      playersDraft.poolForRender !== countRender ||
      !playersDraft.status ||
      !playersDraft.results?.length ||
      countRender >= results.length ||
      (teamPickIndex.length && countRender + 1 >= teamPickIndex[0]) ||
      (fanaticIndexPosition.length && countRender + 1 >= fanaticIndexPosition[0]) ||
      pauseId.length
    ) return;

    const tradeValueTeam = structuredClone(results[countRender]);
    if (tradeValueTeam.player?.player) return;
    const playersAll = playersDraft.results;
    const roundIndex = +tradeValueTeam.round_index_number;
    const playerIteration = +tradeValueTeam.iteration;

    // Authoritative set of players already drafted in THIS iteration (across all teams).
    // draftPlayers is the committed-pick record and is never wiped mid-draft, so this is
    // the reliable enforcement point for the FRC "once per iteration" rule — unlike the
    // pool filters, which the 1.1-board and preferencing paths bypass.
    // Not scoped by round: rule 1 is "once per iteration, round 1 and round 2 together". The first
    // round challenge only ever has round 1, so dropping the round filter changes nothing there.
    const pickedThisIterationIds = new Set(
      draftPlayers
        .filter((p) => p?.player?.id != null && p.iteration === playerIteration)
        .map((p) => p.player.id)
    );

    let player = {};
    let fanaticFlag = false;

    // FanaticBefore forced picks take priority over the pick rules further down.
    // All three challenges replay the user's earlier selections into the slots just before the
    // user's pick (the third round challenge's slots come from thirdRoundReplayBlocks). Mode 2 used to do this only on the !advancedSetting path further down, so with
    // the "Use default" switch left on (its default) the 9 picks before the user were never
    // replaced — that was half of the "iterations 1-5 and 6-10 are not similar" report.
    if (
      !fanaticMode &&
      [1, 2, 3].includes(fanaticChallenge[0]?.mode) &&
      fanaticPickId?.includes(tradeValueTeam.index)
    ) {
      const fanaticBefore = fanaticPlayerBefore.find((item) => item.pick === tradeValueTeam.index);
      if (fanaticBefore) {
        player = fanaticBefore;
        fanaticFlag = true;
      }
    }

    // Fanatic mode replays into every round, and had the same problem: its lookup lived in the
    // !advancedSetting branch below, so with the default settings a test run produced 93 picks
    // and not one replacement. Hoisted here for the same reason. No fanaticPickId check — that
    // list is only populated by fanaticPlayer (modes 1/2), never by fanaticModeBefore.
    if (!player.player && fanaticMode) {
      const fanaticBefore = fanaticPlayerBefore.find((item) => item?.pick === tradeValueTeam.index);
      if (fanaticBefore) {
        player = fanaticBefore;
        fanaticFlag = true;
      }
    }

    // Pick 1.1 hard rule: iteration N = player N on team's draft card (takes priority over the pick rules below)
    if (
      !player.player &&
      fanaticChallenge[0]?.mode === 1 &&
      +tradeValueTeam.round_index_number === 1 &&
      +tradeValueTeam.pick === 1
    ) {
      const iterN = tradeValueTeam.iteration ?? 1;
      const rawBoard = playersDraft.rawTeamBoard ?? [];
      const cardPlayer = rawBoard[iterN - 1];
      if (cardPlayer) {
        player = { player: cardPlayer, playerDepth: iterN };
      }
    }

    // NOTE: forcing the user's prior-iteration picks to be drafted before the user's next
    // pick is handled ONLY at the specific computed slot via the fanaticPickId /
    // fanaticPlayerBefore mechanism above (populated by YourPickModal's `index - pickBefore`
    // formula). Every other simulated team uses normal board logic — see below.

    // Normal FRC simulated pick (mode 1, round 1, not the 1.1 team):
    // take the highest-ranked player on THIS team's OWN draft board who is available.
    // A player is unavailable only if already drafted in the current iteration; a player
    // is also skipped if this team has already drafted him twice across the five iterations.
    // Board rank comes straight from the team-board API, so it is unaffected by the
    // per-iteration ADP shifts that were previously reordering the pool.
    if (
      !player.player &&
      fanaticChallenge[0]?.mode === 1 &&
      roundIndex === 1 &&
      +tradeValueTeam.pick !== 1
    ) {
      const teamNameKey = tradeValueTeam.round.name === '49ers' ? 'ers' : tradeValueTeam.round.name;
      const boardIds = playersDraft.teamBoards?.[teamNameKey] ?? [];
      const allPlayersList = playersDraft.allPlayers ?? [];
      const allById = new Map(allPlayersList.map((p) => [p.id, p]));

      // How many times this team has already drafted each player across all iterations.
      const teamPickCounts = {};
      draftPlayers.forEach((p) => {
        if (p?.player?.id != null && +p.round_index_number === roundIndex && p.round?.name === tradeValueTeam.round.name) {
          teamPickCounts[p.player.id] = (teamPickCounts[p.player.id] || 0) + 1;
        }
      });

      // The team's full ranked preference: its OWN board first (in rank order), then any
      // remaining players by global ranking. Appending off-board players means the board can
      // never be "exhausted", so we never fall through to a pick that ignores these rules.
      const onBoard = new Set(boardIds);
      const offBoardIds = allPlayersList
        .filter((p) => !onBoard.has(p.id))
        .sort((a, b) => (a.ranking ?? 999999) - (b.ranking ?? 999999))
        .map((p) => p.id);
      const rankedIds = [...boardIds, ...offBoardIds];

      // Position exclusivity: a team does not take a second player at a position it has already
      // filled this iteration. The rule lives in draftDisableSettings, but this path is hoisted
      // above it and used to set the pick first — so in FRC round 1 it was bypassed, and a team
      // holding several picks (Jets at 6, 8, 10) could take two QBs in one mock.
      const usedPositions = new Set(
        draftPlayers
          .filter((p) =>
            p?.player?.id != null &&
            p.round?.name === tradeValueTeam.round.name &&
            +p.round_index_number === roundIndex &&
            p.iteration === playerIteration)
          .map((p) => p.player.position)
      );

      let availRank = 0; // rank among players still AVAILABLE (not drafted this iteration)
      let fallback = null; // best eligible player ignoring position, if every position is used
      for (let rank = 0; rank < rankedIds.length; rank++) {
        const id = rankedIds[rank];
        if (pickedThisIterationIds.has(id)) continue;   // drafted this iteration → not available
        availRank++;                                     // this player is still on the board
        if ((teamPickCounts[id] || 0) >= 2) continue;    // available but already taken twice
        const candidate = allById.get(id);
        if (!candidate) continue;
        // playerDepth = the player's rank among AVAILABLE players on this team's card,
        // which drives the grade: highest available → A+ (1), 2-8 → A, etc. As higher
        // players get drafted, the same player's available rank rises → grade improves.
        if (!fallback) fallback = { player: candidate, playerDepth: availRank };
        if (usedPositions.has(candidate.position)) continue; // position already filled
        player = { player: candidate, playerDepth: availRank };
        break;
      }
      // A team that has somehow filled every position still gets a pick rather than stalling.
      if (!player.player && fallback) player = fallback;
    }

    // The challenges and fanatic mode ignore the advanced settings (client, 2026-10-04): their
    // simulated teams always follow the fixed rules. Only a plain mock draft uses the settings.
    if (!player.player) {
      if (fanaticChallenge.length || fanaticMode || tradingSimulatorAction) {
        const result = draftDisableSettings({
          teamUniqPosition,
          playersAll,
          tradeValueTeam,
          roundIndex,
          fanaticChallenge,
          teamUniqPositionFanaticChallenge,
        });
        player = { player: result.player, playerDepth: result.playerChooseIndex };
      } else {
        player = draftPlainSettings({
          draftCardDepth,
          draftRandomnessTeam,
          roundBPA,
          roundDepth,
          playersAll,
          tradeValueTeam,
          teamUniqPosition,
          roundIndex,
        });
      }
    }

    if (!player?.player) return;

    // Final "once per iteration" guard: no matter which path chose the player (1.1 board
    // pick, preferencing, or draftDisableSettings on a possibly-stale pool), a player already
    // taken this iteration by another team must not be re-drafted. Fall back to the best
    // available player in the pool that hasn't been taken this iteration.
    // Mode 2 needs this as much as mode 1 — a Dolphins test run drafted Dylan Stewart at picks 8
    // and 9 of the same iteration, because the pool for pick 9 was built before the pick-8 removal
    // landed. Forced user-history picks go through the guard too: rule 1 outranks rule 8, so a
    // replayed player who is somehow already gone this iteration is swapped rather than duplicated.
    if ((fanaticChallenge[0]?.mode || fanaticMode) && pickedThisIterationIds.has(player.player.id)) {
      const replacement = playersAll.find((p) => !pickedThisIterationIds.has(p.id));
      if (!replacement) return;
      console.log(
        `[SRC] iter ${playerIteration} pick ${tradeValueTeam.index} — ${tradeValueTeam.round.name}: ` +
        `${player.player.player} was already drafted this iteration, swapped for ${replacement.player}` +
        (fanaticFlag ? '  (was a forced user-history pick)' : '')
      );
      player = { player: replacement, playerDepth: replacement.bpa ?? player.playerDepth ?? 1 };
      fanaticFlag = false;
    }

    // Universal pick log (every team, every iteration, every path): show the selected
    // player and how many times THIS team has already drafted him across all iterations.
    // A healthy draft should never show a count of 2 here (that would be a 3rd selection).
    if (fanaticChallenge[0]?.mode === 1 && roundIndex === 1) {
      const priorSelectionsForPlayer = draftPlayers.filter(
        (p) => p?.player?.id === player.player.id && +p.round_index_number === roundIndex
      );
      // FRC rule 2 caps selections PER TEAM ("no team ... more than 2 times"), so this is
      // the number that matters — it must never exceed 2, including this pick.
      const timesByThisTeam = priorSelectionsForPlayer.filter(
        (p) => p.round?.name === tradeValueTeam.round.name
      ).length + 1;
      // Total across ALL teams is NOT capped — different teams may each take him up to twice.
      const timesTotalAllTeams = priorSelectionsForPlayer.length + 1;
      const isException = +tradeValueTeam.pick === 1
        ? '1.1 rule'
        : (fanaticPickId?.includes(tradeValueTeam.index) ? 'forced user-history' : 'normal board');
      // Both warnings below are for normal board picks only: forced user-history and 1.1 picks are
      // exempt from rules 2 and 5 — the rules are about the simulator's own choices. A replay
      // lands the same player in the same slot every iteration, so it passes the cap by design.
      // Positions this team already holds in this round of this iteration.
      const samePosition = draftPlayers.some(
        (p) =>
          p?.player?.position === player.player.position &&
          p.round?.name === tradeValueTeam.round.name &&
          +p.round_index_number === roundIndex &&
          p.iteration === playerIteration
      );
      console.log(
        `[FRC] iter ${playerIteration} pick ${tradeValueTeam.index} — ${tradeValueTeam.round.name} selects ${player.player.player} (${player.player.position}), ` +
        `${player.player.player} selected ${timesByThisTeam} time(s) by ${tradeValueTeam.round.name} (cap 2) ` +
        `[${isException}] — ${timesTotalAllTeams} time(s) total across all teams (not capped)` +
        (timesByThisTeam > 2 && isException === 'normal board' ? '  ⚠️ EXCEEDS MAX TWICE FOR THIS TEAM' : '') +
        (samePosition && isException === 'normal board'
          ? `  ⚠️ ${tradeValueTeam.round.name} ALREADY HAS A ${player.player.position} THIS ITERATION`
          : '')
      );
    }

    // Second / third round challenge diagnostics ([SRC] / [TRC]): shows which slots are forced from
    // the user's history and proves the 2-per-team cap restarts at iteration 6 (block 1 = iters
    // 1-5, block 2 = 6-10).
    if (fanaticChallenge[0]?.mode === 2 || fanaticChallenge[0]?.mode === 3) {
      const tag = fanaticChallenge[0].mode === 3 ? 'TRC' : 'SRC';
      const block = Math.ceil(playerIteration / 5);
      // Counted across all rounds — the cap is 2 per team per 5-iteration block, regardless of
      // round. Forced user-history picks are excluded: rule 8 replays the same player into the same
      // slot every iteration by design, so counting them would flag the feature as a violation.
      const timesThisBlock = draftPlayers.filter(
        (p) => p?.player?.id === player.player.id &&
          p.round?.name === tradeValueTeam.round.name &&
          !p.forcedUserHistory &&
          Math.ceil((+p.iteration || 1) / 5) === block
      ).length + (fanaticFlag ? 0 : 1);
      const source = fanaticFlag ? 'forced user-history' : 'normal board';
      // When the cap leaks, say whether filterPlayers had the right count to act on: `seen` is what
      // it would have read from choosePlayers. seen >= 2 means the pool was not filtered; seen < 2
      // means the pick was not recorded. That distinction is what to chase.
      const seenByFilter = (teamDraftPlayerChoose.find((t) => t.id === tradeValueTeam.round.id)?.choosePlayers ?? [])
        .filter((p) => p.id === player.player.id && Math.ceil((+p._iteration || 1) / 5) === block).length;
      // Rule 1: one player, once per iteration, across all rounds and all 32 teams. Mode 2 has no
      // fallback guard for this (the one below is mode-1 only), so surface it loudly.
      const dupThisIteration = draftPlayers.some(
        (p) => p?.player?.id === player.player.id && +p.iteration === playerIteration
      );
      // Position exclusivity, same window draftDisableSettings uses: this team, this iteration,
      // rounds 1 up to the current one. Forced user-history picks are exempt, and so is pick 1 of a
      // round — draftDisableSettings never checks it (rule 9).
      const samePosition = +tradeValueTeam.pick !== 1 && draftPlayers.some(
        (p) =>
          p?.player?.position === player.player.position &&
          p.round?.name === tradeValueTeam.round.name &&
          +p.iteration === playerIteration &&
          +p.round_index_number <= roundIndex
      );
      console.log(
        `[${tag}] iter ${playerIteration} (block ${block}) r${roundIndex} pick ${tradeValueTeam.index} — ` +
        `${tradeValueTeam.round.name} selects ${player.player.player} (${player.player.position}) [${source}] — ` +
        (!fanaticFlag && samePosition
          ? `⚠️ ${tradeValueTeam.round.name} ALREADY HAS A ${player.player.position} THIS ITERATION — `
          : '') +
        `${timesThisBlock} time(s) by ${tradeValueTeam.round.name} this block (cap 2)` +
        (!fanaticFlag && timesThisBlock > 2
          ? `  ⚠️ EXCEEDS MAX TWICE IN BLOCK (rule 2) — filterPlayers saw ${seenByFilter} prior` +
            (seenByFilter >= 2 ? ', so the pool was NOT filtered' : ', so the pick was NOT recorded')
          : '') +
        (dupThisIteration ? '  ⚠️ ALREADY DRAFTED THIS ITERATION (rule 1)' : '')
      );
    }

    // Plain mock draft diagnostics — no challenge, no fanatic mode, and until now the only mode
    // without logs of its own. Its rules are the simplest, which is exactly why a break is easy
    // to miss: a player is drafted once for the whole draft (no iteration replay, no per-team
    // cap), and a team the user owns must never be auto-picked for.
    if (!fanaticChallenge.length && !fanaticMode) {
      const prior = draftPlayers.filter((p) => p?.player?.id === player.player.id);
      const mine = (teamSelect ?? []).map((t) => t?.name).filter(Boolean);
      const madeSoFar = draftPlayers.filter((p) => p?.player?.player).length + 1;

      // Client rules 2 and 3: highest player on the team's board at a position they have not
      // drafted yet, rounds 1-4; from round 5 the top of the board regardless of position. The
      // advanced settings replace that with a random pick (spots 2 to card depth) for the random
      // teams in their rounds; those still need a new position in rounds 1-4, so that is checked
      // instead. playersAll is the pool the pick functions actually see, already filtered for
      // availability, so walking it the same way reproduces the rule exactly. teamUniqPosition has
      // not been updated for this pick yet (uniqPosition dispatches below), so it is the correct
      // "already drafted".
      const randomPick = player.random === true;
      const usedPositions = new Set(teamUniqPosition[tradeValueTeam.round.name] ?? []);
      const expected = randomPick
        ? null
        : playersAll.find((c) => !(roundIndex <= 4 && usedPositions.has(c.position))) ?? null;
      const ruleBroken = expected && expected.id !== player.player.id;
      const randomRepeat = randomPick && roundIndex <= 4 && usedPositions.has(player.player.position);

      console.log(
        `[PLAIN] r${roundIndex} pick ${tradeValueTeam.index} — ` +
        `${tradeValueTeam.round.name} selects ${player.player.player} (${player.player.position}) | ` +
        `pick ${madeSoFar} of the draft | ` +
        (randomPick ? `advanced settings: random pick, spot ${player.playerDepth} on its board, ` : '') +
        `${roundIndex <= 4 ? 'new position required' : (randomPick ? 'any position' : 'top of board')}, ` +
        `already drafted: ${[...usedPositions].join(', ') || 'none'}` +
        (randomRepeat ? `  ⚠️ RANDOM PICK REPEATS A POSITION (rule 2)` : '') +
        (ruleBroken
          ? `  ⚠️ EXPECTED ${expected.player} (${expected.position}) — highest available${
              roundIndex <= 4 ? ' at an undrafted position' : ''} (rule ${roundIndex <= 4 ? 2 : 3})`
          : '') +
        (prior.length
          ? `  ⚠️ ALREADY DRAFTED by ${prior
              .map((p) => `${p.round?.name} r${p.round_index_number} pick ${p.index}`)
              .join('; ')}`
          : '') +
        (mine.includes(tradeValueTeam.round.name)
          ? `  ⚠️ AUTO-PICKED FOR A USER TEAM (${mine.join(', ')} are the user's)`
          : '')
      );
    }

    // Fanatic mode diagnostics. The replacement is resolved at the top of runAutoPick; it once sat
    // in a branch that never ran, so the first thing to read here is whether ANY pick logs
    // [forced user-history] at all — that is the same hole mode 2 had. Also reports repeats,
    // since no per-team cap exists in fanatic mode, and duplicates within an iteration.
    if (fanaticMode) {
      const timesByTeam = draftPlayers.filter(
        (p) => p?.player?.id === player.player.id && p.round?.name === tradeValueTeam.round.name
      ).length + 1;
      const held = fanaticPlayerBefore.filter((p) => p?.player?.id === player.player.id);
      console.log(
        `[FAN] iter ${playerIteration} r${roundIndex} pick ${tradeValueTeam.index} — ` +
        `${tradeValueTeam.round.name} selects ${player.player.player} ` +
        `[${fanaticFlag ? 'forced user-history' : 'normal board'}] — ` +
        `${timesByTeam} time(s) by ${tradeValueTeam.round.name}` +
        (pickedThisIterationIds.has(player.player.id) ? '  ⚠️ ALREADY DRAFTED THIS ITERATION' : '') +
        (!fanaticFlag && held.length
          ? `  ⚠️ TAKEN EARLY — was held for slot ${held.map((p) => p.pick).join(', ')}`
          : '')
      );
    }

    dispatch(uniqPosition({ name: tradeValueTeam.round.name, position: player.player.position }));
    if (isPositionUnique(teamUniqPositionFanaticChallenge, +tradeValueTeam.round_index_number, tradeValueTeam.iteration, player.player.position, tradeValueTeam.round.name)) {
      dispatch(setTeamUniqPositionFanaticChallenge({
        round: +tradeValueTeam.round_index_number,
        name: tradeValueTeam.round.name,
        position: player.player.position,
        iteration: tradeValueTeam.iteration,
      }));
    }
    tradeValueTeam['player'] = player.player;
    tradeValueTeam['playerDepth'] = player.playerDepth;
    // Marks a replayed user-history pick so the 2-per-block count can ignore it — the same player
    // lands in the same slot every iteration by design (rule 8), which is not a rule-2 violation.
    tradeValueTeam['forcedUserHistory'] = fanaticFlag;
    if (fanaticMode) tradeValueTeam['iteration'] = playerIteration;

    // BPA at the moment of the pick, stamped onto every pick so the Team Picks panel can show
    // where the player stood when he was taken. Looking it up live would drift, because the
    // server re-ranks the board after every pick (client: "where he is at time of pick is best").
    const bpaBoardKey = tradeValueTeam.round.name === '49ers' ? 'ers' : tradeValueTeam.round.name;
    const bpaBoard = playersDraft.teamBoards?.[bpaBoardKey] ?? [];
    tradeValueTeam['boardBpaAtPick'] =
      boardAvailabilityRank(bpaBoard, pickedThisIterationIds, player.player.id);

    // gradeRank = the player's availability rank on THIS team's board (players already drafted
    // this iteration are excluded). This is the single value the result page grades from, and
    // it covers every path uniformly — board pick, 1.1, and forced user-history.
    if (fanaticChallenge[0]?.mode === 1 && roundIndex === 1) {
      const gradeBoard = bpaBoard;
      const gRank = tradeValueTeam['boardBpaAtPick'];
      const gFound = gRank != null;
      tradeValueTeam['gradeRank'] = gFound ? gRank : (player.player.bpa ?? player.playerDepth);

      // TEMP log: gradeRank + the available players on this team's board at pick time.
      const nameById = new Map((playersDraft.allPlayers ?? []).map((p) => [p.id, p.player]));
      const availableNames = gradeBoard
        .filter((id) => !pickedThisIterationIds.has(id))
        .map((id) => nameById.get(id) ?? id);
      console.log(
        `[GRADE] iter ${playerIteration} #${tradeValueTeam.index} ${tradeValueTeam.round.name} → ${player.player.player}`,
        '| gradeRank:', tradeValueTeam['gradeRank'],
        '| board found:', gFound,
        '| available on board (top 12):', availableNames.slice(0, 12)
      );
    }

    // Calculate ADP and set upPlayers BEFORE dispatching — Immer freezes tradeValueTeam after setTradeValue
    const teamNameForADP = tradeValueTeam.round.name === '49ers' ? 'ers' : tradeValueTeam.round.name;
    const teamADPMap = playersDraft.teamADP?.[teamNameForADP] ?? {};
    // Selection number is the team's draft slot, held CONSTANT across the 5 iterations
    // (client confirmed: no iteration reduction, the 1/8 factor is out).
    const selectionAI = tradeValueTeam.index;
    // ADP is seeded from the player's live TEAM-BOARD position, and runs over the AVAILABLE
    // pool only (players already drafted this iteration are off the board).
    const adpBoardIds = playersDraft.teamBoards?.[teamNameForADP] ?? [];
    const teamBoardRankForADP = new Map(adpBoardIds.map((id, idx) => [id, idx + 1]));
    const baseAdp = (p) => teamADPMap[p.id] ?? teamBoardRankForADP.get(p.id) ?? p.ranking ?? 0;
    const allByIdADP = new Map((playersDraft.allPlayers ?? []).map((p) => [p.id, p]));
    const availableBoardADP = (adpBoardIds.length
      ? adpBoardIds.map((id) => allByIdADP.get(id)).filter(Boolean)
      : playersAll
    ).filter((p) => !pickedThisIterationIds.has(p.id));
    const boardWithADP = availableBoardADP.map(p => ({ ...p, adp: baseAdp(p) }));
    const pickedWithADP = { ...player.player, adp: baseAdp(player.player) };
    // The ADP he carried when he was taken, frozen like boardBpaAtPick — the value moves on this
    // very pick, and the board keeps re-ranking afterwards, so a live lookup would drift.
    tradeValueTeam['adpAtPick'] = pickedWithADP.adp;
    const adpMap = calculateADP(selectionAI, boardWithADP, pickedWithADP);
    // [ADP] running-average trace — picked before→after (or "value, no move"), plus every
    // skipped player pulled toward the selection number. Watch the flip and the collapse here.
    {
      const r2 = (n) => Math.round(n * 100) / 100;
      const beforeADP = new Map(boardWithADP.map((p) => [p.id, p.adp]));
      const pickedAfter = adpMap[player.player.id];
      const skipped = Object.entries(adpMap)
        .filter(([id]) => +id !== player.player.id)
        .sort((a, b) => beforeADP.get(+a[0]) - beforeADP.get(+b[0]))
        .slice(0, 8)
        .map(([id, after]) => `${allByIdADP.get(+id)?.player ?? id} ${r2(beforeADP.get(+id))}→${r2(after)}`);
      console.log(
        `[ADP] sel ${selectionAI} it${tradeValueTeam.iteration} ${teamNameForADP} picked ${player.player.player} ` +
        `${r2(pickedWithADP.adp)}${pickedAfter != null ? '→' + r2(pickedAfter) : ' (value, no move)'}` +
        (skipped.length ? ` | skipped↓: ${skipped.join(', ')}` : '')
      );
    }
    if (Object.keys(adpMap).length) {
      dispatch(updateTeamADP({ teamName: teamNameForADP, adpMap }));
      tradeValueTeam.upPlayers = Object.entries(adpMap).map(([id, pricentValue]) => ({ id: +id, pricentValue }));
    }

    const currentKey = tradeValueTeam.index_position ?? tradeValueTeam.index;
    const newResults = results.map((t) =>
      (t.index_position ?? t.index) === currentKey ? tradeValueTeam : t
    );
    dispatch(setTradeValue({ ...tradeValue, results: newResults }));
    dispatch(setDraftPlayersAction(tradeValueTeam));
    dispatch(delPlayersDraft([player.player], tradeValueTeam.iteration));

    // Fanatic challenge post-pick tracking
    if (fanaticChallenge.length > 0) {
      // Every round is recorded, not just round 1 — the "same player at most twice" cap applies
      // to all simulated picks (client rule), and round 2 needs its own count to enforce it.
      dispatch(addPlayerToTeam({
        teamId: tradeValueTeam.round.id,
        playerData: { name: player.player.player, id: player.player.id },
        infoDraft: { round: roundIndex, iteration: tradeValueTeam.iteration, pick: tradeValueTeam.pick },
      }));
      // The old wipe of choosePlayers / infoDraft at the end of iterations 5 and 10 is gone. Its
      // condition (pick === results.length) compared a pick number against the full iterated
      // board length, so it never fired; the 2-per-5-iterations reset is now handled by the block
      // count in filterPlayers, which also keeps the user team's no-repeat history intact.
    }

    if (fanaticFlag) dispatch(fanaticModeBefore({ player, action: 'dec' }));

    pickTimerRef.current = setTimeout(() => {
      dispatch(setCountRender());
    }, 1000 / timeSpeed);
  }, [
    isDrafting, tradeValue, playersDraft, countRender, teamPickIndex, pauseId,
    draftCardDepth, draftRandomnessTeam, roundBPA, roundDepth,
    teamUniqPosition, teamUniqPositionFanaticChallenge, timeSpeed,
    fanaticChallenge, fanaticPickId, fanaticPlayerBefore, fanaticMode, teamDraftPlayerChoose,
    fanaticIndexPosition, tradingSimulatorAction, teamSelectIndex, teamSelect, draftPlayers, dispatch,
  ]);

  useEffect(() => {
    if (isDrafting) runAutoPick();
  }, [tradeValue.mouthing, playersDraft.loading, pauseId, playersDraft.status, isDrafting, runAutoPick]);

  // ── Derived state ────────────────────────────────────────────────────
  const selectedTeam = teamSelect[0] || null;
  const picks = tradeValue?.results || [];

  const roundGroups = (() => {
    const sorted = [...picks].sort((a, b) =>
      (a.index_position ?? a.index ?? 0) - (b.index_position ?? b.index ?? 0)
    );
    const groups = [];
    let currentKey = null;
    for (const pick of sorted) {
      const r = Number(pick.round_index_number);
      const iter = pick.iteration ?? 1;
      const key = `${r}-${iter}`;
      if (key !== currentKey) {
        currentKey = key;
        groups.push({ roundNum: r, iteration: iter, picks: [] });
      }
      groups[groups.length - 1].picks.push(pick);
    }
    return groups;
  })();
  const hasMultipleIterations = roundGroups.some(g => g.iteration > 1);

  // `index` is the pick number within a round, so it repeats across rounds —
  // `index_position` is the board-wide slot and is what orders/identifies a pick.
  const pickKeyOf = (item) => item?.index_position ?? item?.index;

  const picksForTeam = (teamId) =>
    teamId == null
      ? []
      : [...picks]
          .filter((item) => item.round?.id === teamId)
          .sort((a, b) => (pickKeyOf(a) ?? 0) - (pickKeyOf(b) ?? 0));

  // Every team on the board, de-duped off the picks and listed alphabetically
  const teams = (() => {
    const byId = new Map();
    for (const pick of picks) {
      const team = pick.round;
      if (team?.id != null && !byId.has(team.id)) byId.set(team.id, team);
    }
    return [...byId.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  })();

  // The user's own teams, resolved against the board's list so they carry the shape the picks
  // panel expects (id, name, logo) — teamSelect entries come from the config page. Declared after
  // `teams` on purpose: it reads from it.
  const userTeams = (teamSelect ?? [])
    .map((sel) => teams.find((t) => t.name === sel?.name))
    .filter(Boolean);

  // Earliest unfilled pick across ALL the user's teams. No longer shown in the top bar — the
  // panel's own banner says it — but it still marks the upcoming row in the picks panel.
  const nextPick = (() => {
    const mine = userTeams.length ? userTeams : (selectedTeam ? [selectedTeam] : []);
    const match = mine
      .flatMap((t) => picksForTeam(t.id))
      .filter((item) => !item.player)
      .sort((a, b) => (pickKeyOf(a) ?? 0) - (pickKeyOf(b) ?? 0))[0];
    if (!match) return null;
    return {
      roundNum: match.round_index_number,
      pickNum: match.index,
      key: pickKeyOf(match),
      team: match.round,
    };
  })();

  // No team selector any more (client: the panel must match the team on the clock "without user
  // having to click, change display or other"). Team Picks follows the clock; Your Picks follows
  // whichever of the user's teams holds the next unfilled pick, so it is never a team they have
  // finished with.
  const onClockRound = picks[countRender]?.round ?? null;
  const clockTeam = teams.find((t) => t.id === onClockRound?.id) ?? onClockRound;
  // Your Picks keeps its selector and stays where it is put — it does not follow the clock. Only
  // Team Picks tracks the draft; this tab is for looking across the user's own teams at will.
  const userPicksTeam =
    userTeams.find((t) => String(t.id) === String(yourTeamId)) ?? userTeams[0] ?? selectedTeam;

  const picksTeam = (picksTab === 'your' ? userPicksTeam : clockTeam) ?? selectedTeam;
  const teamPicks = picksForTeam(picksTeam?.id);

  const picksTeamName = picksTeam?.name;

  // The BPA column reads boardBpaAtPick, stamped on the pick when it was made. The team board is
  // ONLY a fallback for picks that predate that stamp, so nothing here is needed unless such a
  // pick is actually on screen — an all-upcoming team (or any normally drafted one) needs no
  // board, and used to fetch one and warn for nothing.
  const picksNeedBoardRank = teamPicks.some(
    (item) => item.player?.id != null && item.boardBpaAtPick == null
  );

  // Boards are only cached for teams that have picked, so a team selected from the dropdown may
  // not have one. Fetch on demand — user-initiated, unlike the old load-every-selected-team on
  // mount. The ref stops a failed fetch retrying each time some other board lands in the cache.
  const picksBoardTried = useRef(new Set());
  useEffect(() => {
    if (!picksNeedBoardRank || !picksTeamName) return;
    const key = picksTeamName === '49ers' ? 'ers' : picksTeamName;
    if (playersDraft.teamBoards?.[key]?.length) return;
    if (picksBoardTried.current.has(key)) return;
    picksBoardTried.current.add(key);
    dispatch(refreshTeamBoard({ teamName: picksTeamName }));
  }, [dispatch, picksNeedBoardRank, picksTeamName, playersDraft.teamBoards]);

  const picksTeamBoardRank = useMemo(() => {
    if (!picksNeedBoardRank || !picksTeamName) return null;
    const key = picksTeamName === '49ers' ? 'ers' : picksTeamName;
    const boardIds = playersDraft.teamBoards?.[key];
    if (!boardIds?.length) {
      // Falling back to the site-wide ranking. Says which it is: a name that never matched a
      // cache key, or a board that loaded empty (the fetch in sortPlayersByTeam swallows errors).
      console.warn(
        `[PICKS RANK] no board for "${key}" — showing site ranking.`,
        boardIds ? 'board loaded but empty' : 'no cache entry',
        '| cached teams:', Object.keys(playersDraft.teamBoards ?? {})
      );
      return null;
    }
    return new Map(boardIds.map((id, idx) => [id, idx + 1]));
  }, [picksNeedBoardRank, picksTeamName, playersDraft.teamBoards]);

  const count = tradeValue.mouthing ? countRender + 1 : undefined;
  const isUserTurn =
    isDrafting &&
    (teamPickIndex.includes(count) || status === 'red' || fanaticIndexPosition.includes(count));
  const currentPick = tradeValue.results?.[countRender];
  const currentPickIndex = currentPick?.index_position ?? currentPick?.index ?? -1;

  // Auto-open YourPickModal when it becomes the user's turn
  useEffect(() => {
    if (isUserTurn) setShowYourPick(true);
  }, [isUserTurn]);

  // Mobile sheet: opens for the user's turn and for a trade (including the simulator's random
  // offers), and closes once the user's turn ends, so the board is back on screen while the
  // simulator picks. Leaving a trade does NOT close it: that is usually a tap on Draft a Player,
  // and closing then collapsed the sheet under the user's finger. The user can still open or
  // close it by hand in between.
  const wasUserTurnRef = useRef(isUserTurn);
  useEffect(() => {
    if (isUserTurn || showTrade) setSheetOpen(true);
    else if (wasUserTurnRef.current) setSheetOpen(false);
    wasUserTurnRef.current = isUserTurn;
  }, [isUserTurn, showTrade]);

  // Scroll last picked row into view
  useEffect(() => {
    if (lastPickedRef.current) {
      lastPickedRef.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }
  }, [countRender]);

  // ── Handlers ─────────────────────────────────────────────────────────
  const handleStartDraft = () => {
    // Starting closes the Trade tab if it's open — through closeTrade, so the trade's saved state
    // (teams and their pick lists) doesn't carry into the draft.
    if (!changeTrades) dispatch(closeTrade());
    setIsDrafting(true);
  };

  const handleMakeTrade = () => {
    dispatch(setChangeTrades(false));
    dispatch(setRandomFlag(false));
    if (isDrafting) dispatch(setPauseId('trade'));
    setShowTrade(true);
  };

  // Trades exist only in a plain mock draft — not the challenges or fanatic mode (client rule).
  const tradeAvailable = !fanaticChallenge.length && !fanaticMode;
  // Someone to trade with: a team the user doesn't control that still holds a pick not yet made
  // (the same test as the Trade tab's team list). None when every team is selected, or late in the
  // draft once the other teams have used all their picks.
  const hasTradePartner = tradesTeams.some(
    (t) =>
      !teamSelect.some((s) => s.name === t.name) &&
      (tradeValue.results ?? []).some((p) => p.round?.name === t.name && !p.player)
  );

  // Draft a Player / Trade tabs in the side panel — the only way into a trade. Opening Trade
  // pauses the draft; leaving it is the trade's own Cancel, which resumes it.
  const handlePanelTabChange = (tab) => {
    if (tab === 'trade' && !showTrade) handleMakeTrade();
    if (tab === 'draft' && showTrade) dispatch(closeTrade());
    // Mobile: a tab is in the sheet's header, so tapping one while it's closed opens it.
    setSheetOpen(true);
  };

  // Mobile tabs under the top bar. Full Draft is the round board; the other two are the Team
  // Picks panel on its matching tab (its own tab header is hidden on mobile). Choosing one closes
  // the sheet so the view is actually visible.
  const mobileTab = mobileView === 'board' ? 'full' : picksTab;
  const handleMobileTab = (tab) => {
    if (tab === 'full') {
      setMobileView('board');
    } else {
      setMobileView('picks');
      setPicksTab(tab);
    }
    setSheetOpen(false);
  };

  // Generate the pick indices where auto-random trades will fire during simulation
  const tradeRandomIdGenerate = useCallback(
    (pickIndices) => {
      if (pickIndices.length > 1) {
        const randomNumbers = [];
        const countRandom = Math.ceil(pickIndices.length / 3);
        while (randomNumbers.length < countRandom) {
          const randomIndex = Math.floor(Math.random() * pickIndices.length);
          const randomNumber = pickIndices[randomIndex];
          if (!randomNumbers.includes(randomNumber) && randomNumber !== pickIndices.at(-1)) {
            randomNumbers.push(randomNumber);
          }
        }
        dispatch(setTradeRandomCount(countRandom));
        dispatch(setTradeRandomId(randomNumbers));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teamPickIndex]
  );

  // Seed random trade IDs once randomAccepted is true and none exist yet
  useEffect(() => {
    if (!tradingSimulatorAction && teamPickIndex.length > 0 && tradeRandomId.length === 0 && randomAccepted) {
      tradeRandomIdGenerate(teamPickIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamPickIndex, tradeRandomId]);

  // When teamPickIndex changes (trade happened), remove stale auto-trade IDs for picks no longer owned
  useEffect(() => {
    if (tradeRandomId.length === 0) return;
    const filtered = tradeRandomId.filter((id) => teamPickIndex.includes(id));
    if (filtered.length !== tradeRandomId.length) {
      dispatch(setTradeRandomId(filtered));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamPickIndex]);

  // Auto-open TradeModal when simulation reaches a random-trade pick
  useEffect(() => {
    if (
      !isDrafting ||
      !tradeValue.mouthing ||
      !randomAccepted ||
      !tradeRandomId.length ||
      showTrade
    ) return;
    const currentCount = countRender + 1;
    if (tradeRandomId.includes(currentCount) && currentCount < (teamPickIndex[0] ?? Infinity)) {
      dispatch(setPauseId('trade'));
      dispatch(setRandomFlag(true));
      dispatch(setChangeTrades(false));
      setShowTrade(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countRender, randomAccepted, tradeRandomId, isDrafting]);

  const panelOpen = isDrafting && isUserTurn && showYourPick && !!(playersDraft?.results?.length);

  return (
    <PageShell $panelOpen={panelOpen} style={{ '--sheet-peek': `${isMobile ? sheetPeekH : 0}px` }}>
      <TopBar ref={headerRef}>
        <TopBarRow>
          <TeamInfoLeft>
            <StartDraftBtn
              $isStart={!isDrafting}
              onClick={
                !isDrafting ? handleStartDraft
                // On mobile, Continue Draft is the way back to the pick sheet.
                : isUserTurn ? () => { setShowYourPick(true); setSheetOpen(true); }
                : pauseId.length ? () => dispatch(delPauseId())
                : () => dispatch(setPauseId(countRender))
              }
            >
              {isDrafting && !isUserTurn && !pauseId.length ? <PauseIcon /> : <PlayIcon />}
              {!isDrafting ? 'Start Draft'
                : isUserTurn ? 'Continue Draft'
                : pauseId.length ? 'Resume Draft'
                : 'Pause Draft'}
            </StartDraftBtn>

            <CancelDraftBtn type="button" onClick={onCancel}>
              Cancel Draft
            </CancelDraftBtn>
          </TeamInfoLeft>

        </TopBarRow>

        {isMobile && (
          <MobileViewTabs role="tablist" aria-label="Board view">
            {[
              ['full', 'Full Draft'],
              ['your', 'Your Picks'],
              ['team', 'Team Picks'],
            ].map(([tab, label]) => (
              <MobileViewTab
                key={tab}
                type="button"
                role="tab"
                aria-selected={mobileTab === tab}
                $active={mobileTab === tab}
                onClick={() => handleMobileTab(tab)}
              >
                {label}
              </MobileViewTab>
            ))}
          </MobileViewTabs>
        )}
      </TopBar>

      {!picks.length && (
        <BoardLoaderWrap>
          <BoardLoaderIcon>🏈</BoardLoaderIcon>
          <BoardLoaderTitle>Preparing your draft...</BoardLoaderTitle>
          <BoardLoaderSub>This won't take long.</BoardLoaderSub>
        </BoardLoaderWrap>
      )}

      <ContentRow>
        <BoardColumn $top={headerH} $mobileHidden={mobileView !== 'board'}>
          <BoardHeader>
            <BoardHeaderText>Full Draft</BoardHeaderText>
          </BoardHeader>
          <BoardScroll>
            <BoardWrap $top={headerH}>
              {roundGroups.map(({ roundNum, iteration, picks: roundPicks }) => (
                <div key={`${roundNum}-${iteration}`}>
                  <RoundBanner>
                    <RoundBannerText>
                      ROUND {roundNum}{hasMultipleIterations ? ` ITERATION ${iteration}` : ''}
                    </RoundBannerText>
                  </RoundBanner>

                  {roundPicks.map((item) => {
                    const pickKey = pickKeyOf(item);
                    const displayNum = item.index;
                    const isSelected = teamSelectId.includes(item.round?.index);
                    return (
                      <PickRow
                        key={`${roundNum}-${iteration}-${pickKey}`}
                        $selected={isSelected}
                        $isCurrent={isDrafting && pickKey === currentPickIndex}
                        ref={pickKey === currentPickIndex ? lastPickedRef : null}
                      >
                        <PickLeft>
                          <PickNumCol>
                            <PickNumText>{displayNum}</PickNumText>
                          </PickNumCol>
                          <PickInner>
                            <PickLogoWrap>
                              <PickLogo src={item.round?.logo} alt={item.round?.name} />
                            </PickLogoWrap>
                            <PickNameCol>
                              <PickTeamName>{item.round?.name}</PickTeamName>
                            </PickNameCol>
                            {isDrafting && pickKey === currentPickIndex && !item.player?.player && (
                              <OnTheClockBadge>On the Clock</OnTheClockBadge>
                            )}
                          </PickInner>
                        </PickLeft>

                        <PickRight>
                          <PickSlotA>{item.player?.player || '—'}</PickSlotA>
                          <PickSlotB $color={POSITIONS_COLOR[item.player?.position]}>
                            {item.player?.position || '—'}
                          </PickSlotB>
                        </PickRight>
                      </PickRow>
                    );
                  })}
                </div>
              ))}
            </BoardWrap>
          </BoardScroll>
        </BoardColumn>

        <PickPanelWrap ref={sheetRef} $top={headerH} $sheetOpen={sheetOpen}>
          <YourPickModal
            sheet={isMobile ? {
              open: sheetOpen,
              onToggle: () => setSheetOpen((open) => !open),
              headerRef: sheetHeaderRef,
              swipeHandlers: sheetSwipe,
            } : null}
            playersDraft={playersDraft}
            draftStatus={status}
            setThisId={setThisId}
            isDrafting={isDrafting}
            showTradeTab={tradeAvailable}
            tradeDisabledReason={!hasTradePartner ? 'No other team to trade with' : null}
            activeTab={showTrade ? 'trade' : 'draft'}
            onTabChange={handlePanelTabChange}
            tradePanel={
              showTrade && tradesTeams.length > 0 ? (
                <TradeModal
                  tradesTeams={tradesTeams}
                  teamSelect={teamSelect}
                  attemptsLeft={tradeAttemptsLeft}
                  onRejected={() => setTradeRejections((n) => n + 1)}
                />
              ) : null
            }
            // Before the start the Draft a Player tab has the striped overlay and a "Ready to
            // draft?" bar calling this. The tabs stay usable, so a trade can still be made before
            // the draft starts.
            onStartDraft={handleStartDraft}
          />
        </PickPanelWrap>

        <TeamPicksPanel
          top={headerH}
          team={picksTeam}
          tab={picksTab}
          onTabChange={setPicksTab}
          userTeams={userTeams}
          onTeamChange={setYourTeamId}
          teamPicks={teamPicks}
          boardRankById={picksTeamBoardRank}
          nextPickKey={nextPick?.key}
          showIteration={hasMultipleIterations}
          mobileHidden={mobileView !== 'picks'}
        />
      </ContentRow>

    </PageShell>
  );
};

export default DraftBoard;
